import { PlayoutService } from './../../../services/playout.service';
import { BBPlayerAPI } from './../../../interfaces/bb-player-api';
import { bbDeepCopy } from 'app/shared/utils/bbDeepCopy.util';
import { AssetService } from './../../../services/asset.service';
import { SapiService } from './../../../services/sapi.service';
import { Component, Input, Output, SimpleChanges, ElementRef, ViewChild, EventEmitter, HostListener, HostBinding, OnInit, OnChanges, OnDestroy }
	from '@angular/core';
import { Mediaclip } from '../../../interfaces/mediaclip';
import { Playout } from '../../../interfaces/playout';
import { Publication } from '../../../interfaces/publication';
import { PublicationService } from '../../../services/publication.service';
import { LocationService } from '../../../services/location.service';
import foundation from '../../../../bbjs/foundation.js';
import { map } from 'rxjs/operators';
import { Subscription } from 'rxjs';

export interface PlayerArgs {
	target: any;
	playoutData: any;
	clipData?: any;
}

@Component({
	selector: 'bb-player',
	templateUrl: './bb-player.component.html',
	styleUrls: ['./bb-player.component.scss']
})

export class BbPlayerComponent implements OnInit, OnChanges, OnDestroy {
	@HostBinding('class.size-to-fit') get sizeToFit() {
		return this.inheritDimensions;
	}
	@Input() mediaclip: Mediaclip;
	@Input() publicationUrlOverride: string;
	@Input() playlist: any;
	@Input() autoplay = false;
	@Input() inheritDimensions = false;
	@Input() playoutName: string;
	@Input() assetId: string;
	@Input() forcePlayerCreation: boolean;
	@Input() playout: Playout;
	@Input() playoutOverrides: any = {};
	@Input() transparentBackground: boolean;
	@Input() creationTimeout = 1;
	@Input() allowPlayerRefresh = false;
	@Input() excludeDynamicAssets = false;
	@Output() onPlayerCreated: EventEmitter<any> = new EventEmitter<any>();

	@ViewChild('playerWrapper', { static: true }) playerWrapper: ElementRef;

	public loadingNewClip = false;

	publication: Publication;
	hasValidAssets = true;
	previewPlayout: any;
	playerJSONUrl: string;
	playerApi: BBPlayerAPI;
	lastPlaylist: any;
	minHeight: number;
	baseUrl: string;
	democlip: boolean;
	useJsonForPreview: boolean;
	useSecureAssetPath: boolean;
	previousSkinGeneration: number;
	playerOpacity = 0;

	private subscriptions = new Subscription();

	constructor(
		private sapiService: SapiService,
		private assetService: AssetService,
		private publicationService: PublicationService,
		private playoutService: PlayoutService,
		private locationService: LocationService) {
		this.previewPlayout = require('../../../resources/defaults/previewPlayout.json');

		this.subscriptions.add(this.publicationService.getData().subscribe((publication: Publication) => {
			if (publication) {
				this.publication = publication;
				this.baseUrl = publication.baseurl;
				this.useJsonForPreview = publication.useJsonForPreview;
			}
		}));
	}

	@HostListener('window:resize')
	onWindowResize() {
		this.checkMinHeight();
	}

	ngOnInit() {
		this.playerOpacity = 0;
		if (this.playlist) {
			this.loadPlaylist();
		}

		this.checkMinHeight();
	}

	ngOnDestroy() {
		this.destroyPlayer();
		this.subscriptions.unsubscribe();
	}

	destroyPlayer(cleanupHtml: boolean = true) {
		this.playerOpacity = 0;
		if (this.playerApi) {
			this.playerApi.pause();
			this.playerApi.destruct();
			if (cleanupHtml) {
				this.playerWrapper.nativeElement.innerHtml = '';
			}
			this.playerApi = null;
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.playlist && changes.playlist.currentValue && changes.playlist.previousValue) {
			this.loadPlaylist();
		}

		const forcePlayerCreation = changes.forcePlayerCreation && changes.forcePlayerCreation.currentValue;
		let mediaclipIdChanged = false;
		let mediaclipAssetsChanged = false;
		if (changes.mediaclip && changes.mediaclip.currentValue) {
			mediaclipIdChanged = (changes.mediaclip.previousValue && changes.mediaclip.currentValue.id !== changes.mediaclip.previousValue.id)
				|| changes.mediaclip.currentValue.id;
			mediaclipAssetsChanged = (changes.mediaclip.previousValue && changes.mediaclip.currentValue.assets !== changes.mediaclip.previousValue.assets) ||
				(changes.mediaclip.currentValue.assets);
			this.hasValidAssets = changes.mediaclip.currentValue.assets;
			if (this.playerApi) {
				this.playerApi.pause();
			}
			if (this.mediaclip && this.publication) {
				this.useSecureAssetPath = this.assetService.needsSecurePath(this.publication, this.mediaclip);
			};
		} else if (forcePlayerCreation && this.mediaclip && this.mediaclip.id) {
			mediaclipIdChanged = true;
		}

		if ((this.hasValidAssets || forcePlayerCreation) && (mediaclipIdChanged || mediaclipAssetsChanged)) {
			const baseUrl = this.baseUrl;
			this.democlip = false;
			if (this.mediaclip && this.mediaclip.id && this.mediaclip.id.startsWith('ovp')) {
				this.playoutName = 'default';
				this.democlip = true;
			}
			if (changes.mediaclip && !changes.mediaclip.firstChange) {
				this.destroyPlayer(false);
				this.playerJSONUrl = baseUrl + '/p/' + this.getPlayout() + '/c/' + this.mediaclip.id
					+ '.json?disableKeyboardControls=true&noStats=true&useSession=true';
				this.createPlayer();
			} else {
				if (forcePlayerCreation) {
					if (this.mediaclip) {
						this.sapiService.get('commonclip', this.mediaclip.id).subscribe((mediaclip: Mediaclip) => {
							if (mediaclip) {
								this.mediaclip.assets = mediaclip.assets;
								this.playerJSONUrl = baseUrl + '/p/' + this.getPlayout() + '/c/' + this.mediaclip.id
									+ '.json?disableKeyboardControls=true&noStats=true&useSession=true';
								this.createPlayer();
							}
						});
					}
				} else {
					this.playerJSONUrl = baseUrl + '/p/' + this.getPlayout() + '/c/' + this.mediaclip.id
						+ '.json?disableKeyboardControls=true&noStats=true&useSession=true';
					this.createPlayer();
				}
			}
		}

		if (changes.playout && changes.playout.currentValue) {
			this.updatePlayout();
		}
	}

	checkMinHeight() {
		if (this.mediaclip && this.mediaclip.mediatype === 'audio') {
			this.minHeight = 30;
		} else {
			const pw = this.playerWrapper.nativeElement;
			this.minHeight = pw.getBoundingClientRect().width * 9 / 16;
		}
	}

	updatePlayout() {
		const playoutCopy = Object.assign({}, this.playout);
		playoutCopy.noStats = 'true';
		playoutCopy.disableKeyboardControls = 'true';
		if (this.playerApi) {
			this.playerApi.updatePlayout(playoutCopy);
		} else {
			// hmmm, to do: try to listen to player ready event
			setTimeout(() => {
				if (this.playerApi) {
					this.playerApi.updatePlayout(playoutCopy);
				}
			}, 1000);
		}
	}

	loadPlaylist() {
		if (this.playerApi) {
			if (!this.loadingNewClip && (!this.lastPlaylist || this.lastPlaylist.id !== this.playlist.id)) {

				this.playerApi.load({ cliplistId: this.playlist.id });
				this.loadingNewClip = true;
			}
		} else {
			this.subscriptions.add(this.publicationService.getData().subscribe((publication: Publication) => {
				this.playerJSONUrl = publication.baseurl + '/p/' + this.getPlayout() +
					'/l/' + this.playlist.id + '.json?useSession=true';

				this.createPlayer();
			}));
		}
		this.lastPlaylist = this.playlist;
	}

	loadClip() {
		if (this.playerApi) {
			if (!this.loadingNewClip && this.playerApi.getClipData() && this.playerApi.getClipData().id !== this.mediaclip.id) {
				this.playerApi.load({ clipId: this.mediaclip.id, autoPlay: this.autoplay });
				this.loadingNewClip = true;
			}
		} else {
			if (this.publicationUrlOverride) {
				this.createPublicationPlayer(this.publicationUrlOverride);
			} else {
				this.subscriptions.add(this.publicationService.getData().subscribe((publication: Publication) => {
					this.createPublicationPlayer(publication.baseurl);
				}));
			}
		}
	}

	createPublicationPlayer(publicationUrl: string) {
		if (publicationUrl.indexOf('https://') === -1) {
			publicationUrl = 'https://' + publicationUrl;
		}

		if (!this.mediaclip || !this.mediaclip.assets || !this.mediaclip.assets.length) {
			this.hasValidAssets = false;
		} else {
			this.playerJSONUrl = publicationUrl + '/p/' + this.getPlayout() +
				'/c/' + this.mediaclip.id + '.json?disableKeyboardControls=true&noStats=true&useSession=true';

			let validAssets = 0;

			this.mediaclip.assets.forEach((asset) => {
				if (asset && asset.src) {
					validAssets++;
				}
			});

			if (!validAssets) {
				this.hasValidAssets = false;
			}

			if (this.mediaclip.id.indexOf('ovp') > -1) {
				this.createPlayer();
			} else {

				// retrieve presentation version of this clip, which might contain different assets
				const url = '/json/mediaclip/' + this.mediaclip.id + '?useSession=true';
				this.sapiService.getUrlFromBase(url)
					.pipe(
						map((res: any) => {
							this.mediaclip.assets = res.assets;
							this.createPlayer();
						})
					)
					.subscribe(
						(data: any) => data,
						(error: any) => console.warn(error)
					);
			}
		}
	}

	getPlayout() {
		if (this.playout && this.playout.label && !this.democlip) {
			return this.playout.label;
		} else if (this.playoutName) {
			return this.playoutName;
		} else {
			return 'default';
		}
	}

	createPlayer(): void {
		// construct playerOpts
		const environment = this.locationService.getEnvironment();
		const env = environment && environment.replace(/\s.*$/, ''); // only keep first part
		const cdn = 'cdn' + ((env && env !== 'prod') ? '-' + env : ''); // 'cdn-dev', 'cdn-acc', 'cdn-test', or 'cdn'
		const playerOpts = { playerPath: 'https://' + cdn + '.bluebillywig.com/apps/player/latest/' };

		foundation.loadPlayer(playerOpts, () => {
			// store initial Skin class
			const bbInternal = (window as any).bluebillywig?._internal;
			if (bbInternal && !this.playoutService.initialSkin) {
				this.playoutService.initialSkin = bbInternal.Skin;
			}
			// construct args
			const previewCopy = Object.assign({}, this.previewPlayout);
			const playoutCopy = Object.assign({}, this.playout);
			playoutCopy.publication = `${this.publication?.name}.${env}`;
			const args: PlayerArgs = {
				target: this.playerWrapper.nativeElement,
				playoutData: (this.playout ? playoutCopy : previewCopy)
			};
			if (this.mediaclip && !this.useJsonForPreview) {
				args.clipData = this.mediaclip;
				if (this.useSecureAssetPath) {
					args.clipData.assets = bbDeepCopy(this.mediaclip.assets).map((asset) => {
						asset.src = this.assetService.getAssetUrl(this.mediaclip, asset?.src);
						return asset;
					});
				}
			}

			if (this.excludeDynamicAssets) {
				if (!args.clipData) {
					(args.clipData as Mediaclip) = this.mediaclip;
				}
				args.clipData.assets = bbDeepCopy((args.clipData as Mediaclip).assets).filter(a => !['MP4_HLS', 'MPD'].includes(a.mediatype));
			}

			if (this.playoutOverrides) {
				for (const key in this.playoutOverrides) {
					if (this.playoutOverrides.hasOwnProperty(key) && key.indexOf('$') !== 0) {
						args.playoutData[key] = this.playoutOverrides[key];
					}
				}
			}

			args.playoutData.noStats = 'true';

			const myPlayer = foundation.createPlayer(this.playerJSONUrl, args);
			if (myPlayer) {
				if (this.playerApi) {
					this.playerApi.destruct();
				}

				this.playerApi = myPlayer;

				if (this.playerApi) {
					this.playerApi.on('error', () => {
						this.hasValidAssets = false;
						this.destroyPlayer();
						return;
					});

					this.playerApi.on('changedplayoutdata', () => {
						// player doesn't handle loading a different skin correctly so we reload the player when that happens
						if (this.previousSkinGeneration !== this.playoutService.skinGeneration && this.allowPlayerRefresh) { // also triggers when skinGeneration is undefined
							this.destroyPlayer();
							if (this.playoutService.initialSkin && bbInternal) {
								// resotre initial Skin class
								bbInternal.Skin = this.playoutService.initialSkin;
							}
							setTimeout(() => {
								this.createPlayer();
							}, 0);
						}
						this.previousSkinGeneration = this.playoutService.skinGeneration;
						return;
					});
				}

				this.checkMinHeight();

				if (this.assetId) {
					this.playerApi.on('assetlistchange', () => {
						this.playerApi.setAsset(this.assetId);
					});
				}

				this.playerCreated();
				this.hasValidAssets = true;

				if (this.autoplay) {
					this.playerApi.play();
				}
			}
		});
	}

	playerCreated() {
		setTimeout(() => {
			this.playerOpacity = 1;
		}, this.creationTimeout);
		this.onPlayerCreated.emit(this.playerApi);
		this.playerApi.on('loadedclipdata', () => {
			this.loadingNewClip = false;
		});
	}
}
